import React, { useCallback, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Drawer, Tabs, Tooltip } from 'antd';

import axios from 'axios';
import Alert from 'components/Alert';
import Spin from 'components/Spin';
import { formatDate } from 'helpers/date';
import { tap } from 'rxjs/operators';
import { URL_ORBITA } from 'settings';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import ordersService from 'v2/services/orders';

import Customer from './customer';
import InvoiceItems from './invoiceItems';
import Tracking from './tracking';

enum EInvoiceTabs {
  INVOICE,
  VALUES
}

const Quickview = () => {
  const [tabInvoice, setTabInvoice] = useState<EInvoiceTabs>(EInvoiceTabs.INVOICE);

  const [isLoading] = useObservable(() => ordersService.isLoadingQuickview(), []);
  const [invoice] = useObservable(() => ordersService.getInvoice(), []);
  const [order] = useObservable(() => ordersService.getOrder(), []);
  const [open] = useObservable(() => ordersService.getQuickview(), []);

  const closeQuickView = useCallback(() => ordersService.closeQuickview(), []);

  const generateLabel = useCallback(async () => {
    const response = await axios.get(`${URL_ORBITA}/orders/tags?invoice_id=${invoice.id}&return_url=1`);
    window.open(response.data.data.download_url, '_blank');
  }, [invoice]);

  const openModalRefund = useCallback(() => ordersService.openModalRefund(), []);

  const [onMarkAsSent] = useCallbackObservable(() => {
    return ordersService.markAsSent().pipe(
      tap(() => {
        try {
          toast.success('Entrega marcada como enviada com sucesso!');
        } catch (error) {
          toast.error(error);
        }
      })
    );
  }, []);

  const openModalMarkAsSent = async () => {
    const confirm = await Alert.show({
      message: 'Você tem certeza que deseja marcar essa entrega como enviada?',
      title: 'Atenção',
      confirmation: true
    });
    if (!confirm) return;
    onMarkAsSent();
  };

  const invoiceTabContent = (
    <div className='rounded-sm bg-[#eee] p-4 text-sm [&_p]:mb-0! [&_p]:font-medium'>
      <div className='grid grid-cols-1 items-center gap-4 md:grid-cols-3'>
        <div>
          <p>Código</p> <span>{invoice?.id}</span>
        </div>
        <div>
          <p>Status</p>
          <span title={invoice?.payment_status?.name}>
            {invoice?.payment_status?.id !== 6 && invoice?.payment_status?.id !== 15 && invoice?.payment_status?.name}
            {invoice?.payment_status?.id === 6 && 'Ag. Reembolso'}
            {invoice?.payment_status?.id === 15 && 'Ag. Pagamento'}
          </span>
        </div>
        <div>
          {invoice?.created_at && (
            <>
              <p>Criação</p>
              <span>{formatDate(invoice.created_at)}</span>
            </>
          )}
        </div>
        <div>
          {invoice?.received_at && (
            <>
              <p>Pagamento</p>
              <span>{formatDate(invoice.received_at)}</span>
            </>
          )}
        </div>
        <div>
          {invoice?.credited_at && (
            <>
              <p>Crédito</p>
              <span>{formatDate(invoice.credited_at)}</span>
            </>
          )}
        </div>
        <div>
          {invoice?.expired_at && (
            <>
              <p>Vencimento</p>
              <span>{formatDate(invoice.expired_at)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const valuesTabContent = (
    <div className='rounded-sm bg-[#eee] p-4 text-sm [&_p]:mb-0! [&_p]:font-medium'>
      <div className='grid grid-cols-1 items-center gap-4 md:grid-cols-3'>
        <div>
          <p>Venda</p>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.sale_value} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
        <div>
          <p>Taxa</p>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.rate_amount} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
        <div>
          <p>Comissão</p>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.commission_amount} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
        <div>
          <p>Parceiro</p>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.partner_amount} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
        <div>
          <Tooltip
            placement='top'
            title='A antecipação é descontada diretamente no extrato financeiro (valor apenas para conhecimento)'
          >
            <p>
              Antecipação <InfoCircleOutlined style={{ fontSize: 14 }} />
            </p>
          </Tooltip>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.anticipation_amount} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
        <div>
          <p>Outros</p>
          <>
            <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
              <FormattedNumber format='BRL' value={invoice?.others_values} />
            </PermissionHide>
            <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
              <em>oculto</em>
            </PermissionHide>
          </>
        </div>
      </div>
    </div>
  );

  return (
    <Drawer
      onClose={closeQuickView}
      placement='right'
      open={open}
      closable={false}
      styles={{ body: { padding: 0 } }}
      width='40%'
      rootClassName='max-[1024px]:[&_.ant-drawer-content-wrapper]:w-full! min-[992px]:[&_.ant-drawer-content-wrapper]:max-w-[430px]'
      title={`Fatura: ${invoice?.id}`}
      extra={
        <div
          className='cursor-pointer p-[0.8rem] leading-4 hover:bg-[rgba(0,0,0,0.5)] hover:opacity-60'
          onClick={closeQuickView}
        >
          <CloseOutlined style={{ fontSize: 16, color: '#ccc' }} />
        </div>
      }
    >
      {isLoading && (
        <div className='flex items-center justify-center py-8'>
          <Spin />
        </div>
      )}
      {!isLoading && invoice && (
        <>
          <div className='p-4'>
            <Tabs
              activeKey={String(tabInvoice)}
              onChange={key => setTabInvoice(Number(key) as EInvoiceTabs)}
              items={[
                { key: String(EInvoiceTabs.INVOICE), label: 'Fatura', children: invoiceTabContent },
                { key: String(EInvoiceTabs.VALUES), label: 'Valores', children: valuesTabContent }
              ]}
            />
            <div className='mb-6' />
            <Customer />
            <div className='mb-6' />
            <InvoiceItems />
            <div className='mb-6' />
            <Tracking />
            <div className='mb-6' />
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div>
                <PermissionHide action='myeduzz:minhas-entregas:editar'>
                  <Button type='text' onClick={generateLabel} className='mr-4'>
                    Gerar Etiqueta
                  </Button>
                </PermissionHide>
              </div>
              <div>
                <PermissionHide action='myeduzz:minhas-entregas:reembolsar'>
                  {invoice.payment_status?.id === 3 && <Button onClick={openModalRefund}>Reembolsar fatura</Button>}
                </PermissionHide>
              </div>
              <div className='col-span-full'>
                <PermissionHide action='myeduzz:minhas-entregas:editar'>
                  {order?.status === 'Aguardando Envio' && (
                    <Button type='primary' onClick={openModalMarkAsSent}>
                      Marcar como enviado
                    </Button>
                  )}
                </PermissionHide>
              </div>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default Quickview;
